<template>
    <div>
        {{ timerDisplayTime }}
    </div>
</template>

<script setup lang="ts">
import { ALL_QUEUE_ID, QUEUE_RULE_NO_RULE } from '@/enum/redesignQueueDashboardStatistics'
import { computed, ref, watch } from 'vue'
import { limitThresholdValue, warningThresholdValue } from '@/composables/useSoundsThresholdNotification'
import { useTimer } from '@/composables/useTimer'
import { getMinValue } from '@/helpers/util'
import { getInitialTime } from '@/util/timeUtils'
import { MaxWaitTime, QueuesFromEntities } from '@/helpers/multiQueueDashboardRedesign'
import { activeCallsQueueMap, sharedAllQueueCalls } from '@/composables/useSharedQueueCallsData'

const props = defineProps<{
    queueId: number | string;
    queueName: string;
    warningValue: number;
    limitValue: number;
    filteredQueues: QueuesFromEntities;
    queueRuleId?: number | string;
}>()

const emit = defineEmits<{
    (event: 'update-wait-time-value', payload: MaxWaitTime): void,
    (event: 'clear-calls', payload: number | string): void
}>()

const stats = ref(0)
const thresholdStatusState = ref('')

const {
    timerDisplayTime,
    startTimer,
    timerState,
    clearTimer,
    stopTimer
} = useTimer(false)

const filteredQueueIds = computed(() => {
    const ids = new Set<number | string>()
    for (const q of props.filteredQueues) ids.add(q.queue_id)
    return ids
})

const queueCalls = computed(() => {
    if (props.queueId === ALL_QUEUE_ID) {
        const ids = filteredQueueIds.value
        return sharedAllQueueCalls.value.filter(call => ids.has(call.QueueID))
    }

    const queue = activeCallsQueueMap.value.get(props.queueId as number)
    const calls = queue?.Calls ?? []

    if (props.queueRuleId == null) {
        return calls
    }

    if (props.queueRuleId === QUEUE_RULE_NO_RULE) {
        return calls.filter((call) => !call.queueRule)
    }

    return calls.filter((call) => Number(call.queueRule) === Number(props.queueRuleId))
})

const getStats = () => queueCalls.value.length
    ? getMinValue(queueCalls.value, 'JoinTimeStamp_UTC_CLIENT') || Date.now()
    : Date.now()

function syncThresholdStatus (value: number, forceUpdate = false) {
    if (!value || props.queueId === ALL_QUEUE_ID) return
    if (props.queueRuleId) return

    let thresholdStatus = ''

    if (props.warningValue && value >= props.warningValue) {
        thresholdStatus = warningThresholdValue
    }
    if (props.limitValue && value >= props.limitValue) {
        thresholdStatus = limitThresholdValue
    }

    if (thresholdStatus && (forceUpdate || thresholdStatusState.value !== thresholdStatus)) {
        emit('update-wait-time-value', {
            queueId: props.queueId,
            queueName: props.queueName,
            thresholdStatus,
            value: stats.value,
        })
        thresholdStatusState.value = thresholdStatus
        return
    }
    if (!thresholdStatus && thresholdStatusState.value) {
        emit('clear-calls', props.queueId)
        thresholdStatusState.value = ''
    }
}

watch(() => queueCalls.value.length, (newLength, oldLength) => {
    if (oldLength && oldLength > 0 && newLength === 0) {
        clearTimer()
        thresholdStatusState.value = ''
        emit('clear-calls', props.queueId)
        return
    }

    if (newLength > 0 && (!oldLength || oldLength && newLength < oldLength)) {
        stats.value = getStats()
        stopTimer()
        startTimer({ value: stats.value })
        syncThresholdStatus(getInitialTime(stats.value), true)
    }
}, { immediate: true })

watch(() => timerState.value, (newVal) => {
    syncThresholdStatus(newVal)
})

watch(
    () => [props.warningValue, props.limitValue],
    () => {
        syncThresholdStatus(timerState.value, true)
    }
)
</script>

<style scoped lang="scss">
</style>
